import Cookies from 'js-cookie';
import { FC, memo, MouseEvent, useCallback, useMemo } from 'react';

import { EmptyGameCard } from './EmptyGameCard';
import { GameCard } from './GameCard';
import { LockedGameCard } from './LockedGameCard';
import { SmallGameCard } from './SmallGameCard';
import { SmallLockedGameCard } from './SmallLockedGameCard';
import { GameV1 } from '@/api/games';
import { COOKIE_NAME } from '@/helpers/cookie';
import { Modals } from '@/helpers/modals';
import { TCardMode } from '@/modules/game/types';
import { useCommonStore, useModalsStore } from '@/store';

interface IGameCardFactoryProps {
  game?: GameV1;
  isEmpty?: boolean;
  isSmall?: boolean;
  withoutBottom?: boolean;
  mode?: TCardMode;
  onPlayClick?: (game: GameV1) => void;
  onDemoClick?: (game: GameV1) => void;
}

export const GameCardFactory: FC<IGameCardFactoryProps> = memo(
  ({
    game,
    isEmpty,
    isSmall,
    mode = 'dark',
    withoutBottom = false,
    onPlayClick,
    onDemoClick,
  }) => {
    const user = useCommonStore.use.user();
    const isAuthenticated = useCommonStore.use.isAuthenticated();

    const open = useModalsStore.use.open();

    const isLocked = useMemo(() => {
      if (!user || !game) return false;
      return !game.currencies[user.currency];
    }, [user, game]);

    const handlePlayClick = useCallback(
      (event: MouseEvent) => {
        if (!game) return;

        onPlayClick && onPlayClick(game);

        if (!isAuthenticated) {
          event.preventDefault();
          const isRegistered =
            Cookies.get(COOKIE_NAME.IS_REGISTERED) === 'true';

          useCommonStore.setState({
            afterLoginRedirectUrl: encodeURI(`/game/${game.fullPath}`),
          });
          open(isRegistered ? Modals.Login : Modals.Registration);
          return;
        }
      },
      [game, isAuthenticated, onPlayClick, open]
    );

    const Card = useMemo(() => {
      switch (true) {
        case !!game && isLocked && isSmall:
          return <SmallLockedGameCard game={game} />;
        case !!game && isLocked:
          return (
            <LockedGameCard
              game={game}
              mode={mode}
              withoutBottom={withoutBottom}
            />
          );
        case isEmpty:
          return <EmptyGameCard mode={mode} />;
        case !!game && isSmall:
          return <SmallGameCard game={game} />;
        case !!game:
          return (
            <GameCard
              game={game}
              mode={mode}
              withoutBottom={withoutBottom}
              onPlayClick={handlePlayClick}
              onDemoClick={() => onDemoClick && onDemoClick(game)}
            />
          );
        default:
          return null;
      }
    }, [
      game,
      isEmpty,
      isSmall,
      mode,
      withoutBottom,
      isLocked,
      onPlayClick,
      onDemoClick,
    ]);

    return Card;
  }
);
